import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BuildStore from '../../../redux/builds';
import { nuc } from '../../../utils';
import type { AsyncStatus } from '../../../types';

export type Query = {
  page: number;
  pageSize: number;
  startDate: string;
  endDate: string;
};

export type BuildListMode = 'REVISION' | 'PROJECT';

type UseBuildListProps = {
  query?: Partial<Query>;
  mode: BuildListMode;
  locator: string;
};

const getBuildQuery = ({ query, mode, locator }: UseBuildListProps) => {
  const buildQuery = {
    sort: '-createdAt',
    ...query,
  } as Query & { sort: string; locator?: string; projectId?: string };

  if (mode === 'REVISION') {
    buildQuery.locator = nuc(locator);
  } else {
    buildQuery.projectId = nuc(locator);
  }

  return buildQuery;
};

const useBuildList = ({
  query = {},
  mode,
  locator,
}: UseBuildListProps) => {
  const dispatch = useDispatch();

  const { page, pageSize, startDate, endDate } = query;

  const count = useSelector(BuildStore.selectors.getBuildCount);
  const countStatus: AsyncStatus = useSelector(BuildStore.selectors.getBuildCountStatus);

  const getBuildsFn = mode === 'REVISION'
    ? BuildStore.selectors.getCurrentRevisionBuilds
    : BuildStore.selectors.getCurrentProjectBuilds;

  const builds = useSelector(getBuildsFn(locator));
  const buildStatus: AsyncStatus = useSelector(BuildStore.selectors.getStatus);

  // Only update count if non-pagination filters change (start and end date)
  useEffect(() => {
    const buildQuery = getBuildQuery({ query: { startDate, endDate }, mode, locator });
    dispatch(BuildStore.actions.fetchAllCounts(buildQuery));
  }, [locator, mode, startDate, endDate, dispatch]);

  // Refetch builds anytime query changes
  useEffect(() => {
    const buildQuery = getBuildQuery({ query: { startDate, endDate, page, pageSize }, mode, locator });
    dispatch(BuildStore.actions.fetchAll(buildQuery));
  }, [locator, mode, startDate, endDate, page, pageSize, dispatch]);

  return {
    builds,
    buildStatus,
    count,
    countStatus,
  };
};

export default useBuildList;
